import { Suspense, type ReactNode } from "react";

import { Link, useSearchParams } from "react-router-dom";

import { OnboardingChecklist } from "@/features/onboarding/OnboardingChecklist";
import { SidebarTourMount } from "@/features/onboarding/SidebarTourMount";
import { AiChatWidget } from "@rook/shared/components/AiChatWidget";
import { AppShell, RookWordmark, WhatsNew } from "@rook/ui";

import { Sidebar } from "./Sidebar";
import { AccountMenu, TopbarRight } from "./Topbar";

// Portal version surfaced in the sidebar's bottom WhatsNew row. Kept as
// a constant here (the app's package.json version) so there's a single
// place to bump it; no per-route context exposes it today.
const APP_VERSION = "3.0.0";

const RouteFallback = () => (
  <div className="flex h-full items-center justify-center text-sm text-rook-olive">
    Loading…
  </div>
);

/**
 * The /home/* layout — the @rook/ui v0.20 `<AppShell>` frame: a
 * persistent left sidebar (brand top · nav · user pinned bottom ·
 * version row) and a SLIM top bar for global selectors. The leaf
 * route's element renders inside the `<Suspense>` boundary so a route
 * chunk that hasn't downloaded yet shows a calm skeleton instead of a
 * blank screen.
 *
 * Slot mapping (was: legacy `sidebar`/`topbar` props):
 *   brand       → Rook wordmark lockup, links to /home
 *   nav         → <Sidebar/> (SidebarNav + SidebarNavItem rows)
 *   user        → <AccountMenu/> (design-system UserMenu, sidebar bottom)
 *   meta        → <WhatsNew/> version row at the very bottom
 *   topbar      → (page context — none yet; intentionally empty)
 *   topbarRight → <TopbarRight/> (Docs + Env switcher)
 *
 * Children come from the parent (HomeRoute), which uses RR's
 * `<Outlet />` to inject the matched leaf.
 *
 * Content gutter: padding + max-width + centering live on the inner
 * wrapper here (NOT inside each view), so every view is consistent
 * without each one having to remember.
 */
export const HomeShell = ({ children }: { children: ReactNode }) => {
  // Tour re-launch trigger: Account view sends the customer here
  // with `?tour=restart` after clearing the seen-flag. The query
  // param is the simplest "tell the tour to fire again" signal —
  // no cross-feature context, no global event bus.
  const [searchParams] = useSearchParams();
  const autostartTour = searchParams.get("tour") === "restart";

  return (
    <>
      <AppShell
        brand={
          <Link to="/home" className="flex items-center gap-2 px-1">
            <RookWordmark tone="gradient" aria-label="Rook" className="h-6 w-auto" />
          </Link>
        }
        nav={<Sidebar />}
        user={<AccountMenu />}
        meta={
          <WhatsNew
            version={APP_VERSION}
            hasUpdate={false}
            // No-op for now — wire to a real changelog surface
            // (route/modal) once the portal ships one.
            onClick={() => {}}
          />
        }
        topbarRight={<TopbarRight />}
      >
        <div className="mx-auto w-full max-w-[1440px]">
          <Suspense fallback={<RouteFallback />}>{children}</Suspense>
        </div>
      </AppShell>
      <SidebarTourMount autostart={autostartTour} />
      {/*
        Bottom-LEFT floating onboarding checklist (CPF-UIR-112).
        Bottom-RIGHT is the AI chat widget below — opposite corners so
        they never fight for the same viewport area. Self-hides when all
        items complete OR the user dismisses.
      */}
      <OnboardingChecklist />
      {/*
        Bottom-RIGHT AI chat widget (CPF-NFR-033) — ROOK's AI replacement
        for the legacy Intercom bubble. Control-plane consumer: one chat
        backend over HTTPS, env-agnostic (same widget in prod + sandbox).
        Disabled by default (renders null) until VITE_AI_CHAT_* is set.
      */}
      <AiChatWidget />
    </>
  );
};
